import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { MaskedInput } from '@/components/ui/masked-input';
import { EntityFormDialog } from '@/shared/components';
import { useSession } from '@/hooks/useSession';
import { useCreateSite, useAccounts } from '../../hooks';
import type { CreateSiteInput } from '../../types';
import { SITE_TAGS } from '../../types';
import { TagInput } from '../TagInput';

interface CreateSiteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSiteModal({ open, onOpenChange }: CreateSiteModalProps) {
  const { t } = useTranslation('sites');
  const createSiteMutation = useCreateSite();
  const { data: accounts, isLoading: isLoadingAccounts } = useAccounts();
  const { user: sessionUser } = useSession();
  const isFullAdmin = !!(sessionUser?.postgres_roles?.includes('full_admin') || sessionUser?.postgres_roles?.includes('director'));

  // Form state
  const [accountId, setAccountId] = useState('');
  const [name, setName] = useState('');
  const [abbreviations, setAbbreviations] = useState<string[]>([]);
  const [phone, setPhone] = useState('');
  const [phoneExtension, setPhoneExtension] = useState('');
  const [email, setEmail] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [stateProvince, setStateProvince] = useState('Québec');
  const [country, setCountry] = useState('Canada');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const resetForm = () => {
    setAccountId('');
    setName('');
    setAbbreviations([]);
    setPhone('');
    setPhoneExtension('');
    setEmail('');
    setAddressLine1('');
    setCity('');
    setPostalCode('');
    setStateProvince('Québec');
    setCountry('Canada');
    setDescription('');
    setSelectedTags([]);
  };

  const isTransporteur = selectedTags.includes('transporteur');

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      }
      // If selecting transporteur, remove billing and shipping
      if (tag === 'transporteur') {
        return [...prev.filter((t) => t !== 'billing' && t !== 'shipping'), tag];
      }
      return [...prev, tag];
    });
  };

  const handleSubmit = async () => {
    const data: CreateSiteInput = {
      accountId: accountId,
      name: name.trim(),
      abbreviations,
      phone: phone.trim() || undefined,
      phoneExtension: phoneExtension.trim() || undefined,
      email: email.trim() || undefined,
      addressLine1: addressLine1.trim() || undefined,
      city: city.trim() || undefined,
      postalCode: postalCode.trim() || undefined,
      stateProvince: stateProvince.trim() || undefined,
      country: country.trim() || undefined,
      description: description.trim() || undefined,
      tags: selectedTags,
    };

    try {
      await createSiteMutation.mutateAsync(data);
      resetForm();
      onOpenChange(false);
    } catch {
      // Error handling is done in the mutation hook
    }
  };

  const isValid = !!accountId && !!name.trim();

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('edit.addNew', 'Create Site')}
      description="Add a new site to the platform."
      onSubmit={handleSubmit}
      isSubmitting={createSiteMutation.isPending}
      saveLabel={t('toolbar.create', 'Create')}
      cancelLabel={t('edit.cancel', 'Cancel')}
      saveDisabled={!isValid}
    >
      <div className="space-y-4">
          {/* Account Selection */}
          <div className="grid gap-2">
            <Label htmlFor="account">Account *</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Select account..." />
              </SelectTrigger>
              <SelectContent>
                {isLoadingAccounts ? (
                  <SelectItem value="" disabled>Loading...</SelectItem>
                ) : (
                  accounts?.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Name */}
          <div className="grid gap-2">
            <Label htmlFor="name">{t('edit.name', 'Name')} *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          {/* Abbreviations */}
          <div className="grid gap-2">
            <Label>{t('edit.abbreviations', 'Abbreviations')}</Label>
            <TagInput
              id="abbreviations"
              value={abbreviations}
              onChange={setAbbreviations}
              placeholder="Type and press Enter"
            />
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="phone">{t('edit.phone', 'Phone')}</Label>
              <MaskedInput
                id="phone"
                mask="(000) 000-0000"
                value={phone}
                onAccept={(val) => setPhone(val)}
                placeholder="(___) ___-____"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phoneExtension">Ext.</Label>
              <Input
                id="phoneExtension"
                value={phoneExtension}
                onChange={(e) => setPhoneExtension(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">{t('edit.email', 'Email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {/* Address */}
          <div className="grid gap-2">
            <Label htmlFor="addressLine1">{t('edit.address', 'Address')}</Label>
            <Input
              id="addressLine1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="city">{t('edit.city', 'City')}</Label>
              <Input
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="postalCode">{t('edit.postcode', 'Postcode')}</Label>
              <Input
                id="postalCode"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="stateProvince">{t('edit.state_province_county', 'Province')}</Label>
              <Input
                id="stateProvince"
                value={stateProvince}
                onChange={(e) => setStateProvince(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="country">{t('edit.country', 'Country')}</Label>
              <Input
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
            </div>
          </div>

          {/* Tags - only visible for full admin */}
          {isFullAdmin && (
            <div className="grid gap-2">
              <Label>{t('edit.tags', 'Tags')}</Label>
              <div className="flex flex-wrap gap-3">
                {SITE_TAGS.map((tag) => {
                  const isDisabledByTransporteur =
                    isTransporteur && (tag === 'billing' || tag === 'shipping');
                  return (
                    <div key={tag} className="flex items-center space-x-2">
                      <Checkbox
                        id={`create-tag-${tag}`}
                        checked={selectedTags.includes(tag)}
                        onCheckedChange={() => handleTagToggle(tag)}
                        disabled={isDisabledByTransporteur}
                      />
                      <label
                        htmlFor={`create-tag-${tag}`}
                        className={`text-sm cursor-pointer ${
                          isDisabledByTransporteur ? 'text-muted-foreground' : ''
                        }`}
                      >
                        {tag}
                      </label>
                    </div>
                  );
                })}
              </div>
              {isTransporteur && (
                <p className="text-xs text-muted-foreground">
                  Billing and shipping tags are disabled when transporteur is selected.
                </p>
              )}
            </div>
          )}

          {/* Description */}
          <div className="grid gap-2">
            <Label>{t('edit.description', 'Description')}</Label>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Enter description..."
              minHeight="80px"
            />
          </div>

      </div>
    </EntityFormDialog>
  );
}
